/* eslint-disable @typescript-eslint/no-unused-vars */
/**
 * Utility for PWA version management
 */

// Current version from package.json
export const APP_VERSION = process.env.NEXT_PUBLIC_APP_VERSION || "0.1.0";

// Storage keys
const STORAGE_KEYS = {
  CURRENT_VERSION: "tms_current_version",
  LAST_CHECK: "tms_last_version_check",
  UPDATE_AVAILABLE: "tms_update_available",
} as const;

/**
 * Get stored version information
 */
export function getStoredVersion(): {
  currentVersion: string;
  lastCheck: number;
  updateAvailable: boolean;
} {
  if (typeof window === "undefined") {
    return {
      currentVersion: APP_VERSION,
      lastCheck: 0,
      updateAvailable: false,
    };
  }

  return {
    currentVersion:
      localStorage.getItem(STORAGE_KEYS.CURRENT_VERSION) || APP_VERSION,
    lastCheck: parseInt(localStorage.getItem(STORAGE_KEYS.LAST_CHECK) || "0"),
    updateAvailable:
      localStorage.getItem(STORAGE_KEYS.UPDATE_AVAILABLE) === "true",
  };
}

/**
 * Update stored version information
 */
function updateStoredVersion(version: string, updateAvailable = false): void {
  if (typeof window === "undefined") return;

  localStorage.setItem(STORAGE_KEYS.CURRENT_VERSION, version);
  localStorage.setItem(STORAGE_KEYS.LAST_CHECK, Date.now().toString());
  localStorage.setItem(
    STORAGE_KEYS.UPDATE_AVAILABLE,
    updateAvailable.toString()
  );
}

/**
 * Check if there's a new version of the app available
 * Uses hybrid approach: localStorage for offline + manifest for accuracy
 * @param currentVersion - Optional manually provided current version
 * @param forceCheck - Force check even if recently checked
 * @returns Promise<boolean> - True if a new version is available
 */
export async function checkForUpdates(
  currentVersion = APP_VERSION,
  forceCheck = false
): Promise<boolean> {
  try {
    const stored = getStoredVersion();
    // const now = Date.now();
    // const fiveMinutes = 5 * 60 * 1000;

    // // If we have a cached result and it's recent, use it (unless forced)
    // if (!forceCheck && stored.lastCheck && (now - stored.lastCheck) < fiveMinutes) {
    //   return stored.updateAvailable;
    // }

    console.log("Checking for updates...");
    console.log(
      `Current version: ${currentVersion} | Stored version: ${stored.currentVersion}`
    );
    if (stored.currentVersion !== currentVersion) {
      updateStoredVersion(currentVersion, true);
      return true;
    }

    // No update available
    updateStoredVersion(currentVersion, false);
    return false;
  } catch (error) {
    console.error("Failed to check for updates:", error);
    return false;
  }
}

/**
 * Force refresh the PWA to get the latest version
 */
export function updateApplication(): void {
  // Clear update flag when applying update
  if (typeof window !== "undefined") {
    localStorage.setItem(STORAGE_KEYS.UPDATE_AVAILABLE, "false");
  }

  if ("serviceWorker" in navigator) {
    navigator.serviceWorker.ready.then(registration => {
      registration.update().then(() => {
        window.location.reload();
      });
    });
  } else {
    window.location.reload();
  }
}

/**
 * Check if app was recently updated
 * Useful for showing "What's new" messages
 */
export function wasRecentlyUpdated(): boolean {
  const stored = getStoredVersion();
  const now = Date.now();
  const oneHour = 60 * 60 * 1000;

  return Boolean(stored.lastCheck && now - stored.lastCheck < oneHour);
}
